import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useLocalizedNavigate as useNavigate } from "@/hooks/useLocalizedNavigate";
import { useTranslation } from "react-i18next";
import { ArrowLeft, FileText, Star, Eye, ShieldCheck } from "lucide-react";
import { PublicShell } from "@/components/layouts/PublicShell";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Seo } from "@/components/seo/Seo";
import { supabase } from "@/integrations/supabase/client";
import { useLocalizedField } from "@/hooks/useLocalizedField";
import { resolveMobileMoney } from "@/lib/mobileMoney";
import { MobileMoneyButton } from "@/components/commerce/MobileMoneyButton";
import { Price } from "@/components/pricing/Price";
import { LeadMagnetGate } from "@/components/downloads/LeadMagnetGate";
import { useEntitlements } from "@/hooks/useEntitlements";

interface DownloadRow {
  id: string;
  slug: string;
  title_en: string | null;
  title_es: string | null;
  title_fr: string | null;
  title_pt: string | null;
  description_en: string | null;
  description_es: string | null;
  description_fr: string | null;
  description_pt: string | null;
  cover_image_url: string | null;
  category: string | null;
  file_kind: string | null;
  price_eur: number;
  sample_pages: number | null;
  total_pages: number | null;
  file_size_mb: number | null;
  author_id: string | null;
  payment_link_mobile_ngn?: string | null;
  payment_link_mobile_xof?: string | null;
  payment_link_mobile_kes?: string | null;
  payment_link_mobile_brl?: string | null;
  access_mode?: string | null;
  lead_magnet?: boolean | null;
  gate_copy_en?: string | null;
  gate_copy_fr?: string | null;
  gate_copy_es?: string | null;
  gate_copy_pt?: string | null;
}

export default function DownloadsDetail() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const pick = useLocalizedField();
  const [row, setRow] = useState<DownloadRow | null | "missing">(null);
  const [related, setRelated] = useState<DownloadRow[]>([]);
  const [authorName, setAuthorName] = useState<string>("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const ents = useEntitlements();
  const passActive = ents.isAuthed && ents.hasFounderPass;

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("downloads")
        .select("*")
        .eq("slug", slug!)
        .eq("published", true)
        .maybeSingle();
      if (!data) { setRow("missing"); return; }
      setRow(data as DownloadRow);
      if (data.author_id) {
        const { data: prof } = await supabase
          .from("profiles")
          .select("first_name, last_name")
          .eq("id", data.author_id)
          .maybeSingle();
        if (prof) setAuthorName(`${prof.first_name ?? ""} ${prof.last_name ?? ""}`.trim());
      }
      const { data: rel } = await supabase
        .from("downloads")
        .select("id, slug, title_en, title_es, title_fr, title_pt, description_en, description_es, description_fr, description_pt, cover_image_url, category, file_kind, price_eur, sample_pages, total_pages, file_size_mb, author_id")
        .eq("published", true)
        .eq("category", data.category ?? "")
        .neq("id", data.id)
        .limit(3);
      setRelated((rel ?? []) as DownloadRow[]);
    })();
  }, [slug]);

  if (row === null) {
    return <PublicShell><div className="container py-10"><Skeleton className="h-72 w-full" /></div></PublicShell>;
  }
  if (row === "missing") {
    return (
      <PublicShell>
        <div className="container py-16 text-center">
          <h1 className="text-2xl font-semibold">{t("downloads.notFound", "Resource not found")}</h1>
          <Button className="mt-4" asChild><Link to="/downloads">{t("downloads.backToList", "Back to library")}</Link></Button>
        </div>
      </PublicShell>
    );
  }

  const title = pick(row, "title");
  const description = pick(row, "description");
  const isFree = Number(row.price_eur) === 0;
  const isGated = row.access_mode === "email_gated" || row.lead_magnet === true;
  const lng = (i18n.language || "en").slice(0, 2);
  const gateCopy = (row as unknown as Record<string, string | null | undefined>)[`gate_copy_${lng}`] ?? row.gate_copy_en ?? null;

  return (
    <PublicShell>
      <Seo title={`${title} — Bootpreneurs`} description={description?.slice(0, 160)} type="product" image={row.cover_image_url ?? undefined} />
      <div className="container py-6">
        <Button variant="ghost" size="sm" asChild className="mb-4 -ml-2">
          <Link to="/downloads"><ArrowLeft className="mr-1 h-4 w-4" />{t("downloads.backToList", "Back to library")}</Link>
        </Button>

        <div className="grid gap-8 lg:grid-cols-[1fr_360px]">
          <div>
            <div className="aspect-[16/9] overflow-hidden rounded-lg border border-border bg-gradient-to-br from-primary/15 to-accent/15">
              {row.cover_image_url ? (
                <img src={row.cover_image_url} alt={title} className="h-full w-full object-cover" />
              ) : (
                <div className="grid h-full place-items-center"><FileText className="h-20 w-20 text-primary/40" /></div>
              )}
            </div>
            <div className="mt-6 flex flex-wrap items-center gap-2">
              {row.file_kind && <Badge variant="secondary" className="capitalize">{row.file_kind}</Badge>}
              {row.category && <Badge variant="outline" className="capitalize">{row.category}</Badge>}
              {isFree && <Badge>{t("downloads.free", "Free")}</Badge>}
            </div>
            <h1 className="mt-3 font-display text-3xl font-bold">{title}</h1>
            {authorName && <p className="mt-2 text-sm text-muted-foreground">{t("downloads.by", "by")} {authorName}</p>}

            <Tabs defaultValue="overview" className="mt-6">
              <TabsList>
                <TabsTrigger value="overview">{t("downloads.tabs2.overview", "Overview")}</TabsTrigger>
                <TabsTrigger value="toc">{t("downloads.tabs2.toc", "Contents")}</TabsTrigger>
                <TabsTrigger value="reviews">{t("downloads.tabs2.reviews", "Reviews")}</TabsTrigger>
              </TabsList>
              <TabsContent value="overview" className="prose prose-sm max-w-none dark:prose-invert">
                <p className="whitespace-pre-wrap text-foreground">{description || t("downloads.noDescription", "No description available.")}</p>
              </TabsContent>
              <TabsContent value="toc">
                <Card><CardContent className="py-6 text-sm text-muted-foreground">{t("downloads.tocSoon", "Detailed table of contents coming soon. The full file contains")} {row.total_pages ?? "?"} {t("downloads.pages", "pages")}.</CardContent></Card>
              </TabsContent>
              <TabsContent value="reviews">
                <Card><CardContent className="py-6 text-sm text-muted-foreground">{t("downloads.noReviews", "Be the first to review this resource.")}</CardContent></Card>
              </TabsContent>
            </Tabs>
          </div>

          <aside className="space-y-4">
            <Card className="sticky top-20">
              <CardContent className="space-y-4 p-5">
                <div>{isGated || isFree ? <span className="text-3xl font-bold">{t("downloads.free", "Free")}</span> : <Price size="hero" eurCents={Math.round(Number(row.price_eur) * 100)} />}</div>
                {!isGated && !isFree && passActive && (
                  <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-xs">
                    <Badge>{t("downloads.passDiscount", "Founder Pass −20%")}</Badge>
                    <span className="text-muted-foreground">
                      {t("downloads.passDiscountNote", "Applied automatically at checkout")}
                    </span>
                  </div>
                )}
                {isGated ? (
                  <LeadMagnetGate
                    downloadId={row.id}
                    downloadSlug={row.slug}
                    downloadTitle={title || row.slug}
                    locale={lng}
                    gateCopy={gateCopy}
                  />
                ) : (() => {
                  const mm = !isFree ? resolveMobileMoney(row, { locale: i18n.language }) : null;
                  return (
                    <>
                      {mm && (
                        <MobileMoneyButton
                          link={mm.link}
                          currency={mm.currency}
                          productId={row.id}
                          productKind="download"
                          className="w-full"
                        />
                      )}
                      <Button
                        className="w-full"
                        size="lg"
                        variant={mm ? "outline" : "default"}
                        onClick={() => navigate(`/downloads/${row.slug}/checkout`)}
                      >
                        {isFree
                          ? t("downloads.getFree", "Get for free")
                          : mm
                            ? t("commerce.payWithCard", "Pay with card")
                            : t("downloads.buyNow", "Buy now")}
                      </Button>
                    </>
                  );
                })()}
                {(row.sample_pages ?? 0) > 0 && (
                  <Button variant="ghost" className="w-full" onClick={() => setPreviewOpen(true)}>
                    <Eye className="mr-2 h-4 w-4" />{t("downloads.lookInside", "Look inside")} ({row.sample_pages}p)
                  </Button>
                )}
                <ul className="space-y-2 text-sm text-muted-foreground">
                  {row.total_pages && <li className="flex items-center gap-2"><FileText className="h-4 w-4" />{row.total_pages} {t("downloads.pages", "pages")}</li>}
                  {row.file_size_mb && <li className="flex items-center gap-2"><FileText className="h-4 w-4" />{row.file_size_mb} MB</li>}
                  <li className="flex items-center gap-2"><Star className="h-4 w-4" />4.8 / 5</li>
                  <li className="flex items-center gap-2"><ShieldCheck className="h-4 w-4" />{t("downloads.refund", "14-day refund")}</li>
                </ul>
              </CardContent>
            </Card>
          </aside>
        </div>

        {related.length > 0 && (
          <section className="mt-12">
            <h2 className="mb-4 font-display text-xl font-semibold">{t("downloads.related", "Related resources")}</h2>
            <div className="grid gap-4 sm:grid-cols-3">
              {related.map((r) => (
                <Link key={r.id} to={`/downloads/${r.slug}`}>
                  <Card className="h-full transition-all hover:border-primary/40">
                    <CardContent className="p-4">
                      <h3 className="line-clamp-2 font-medium">{pick(r, "title")}</h3>
                      <div className="mt-2">
                        {Number(r.price_eur) === 0
                          ? <span className="text-sm font-semibold">{t("downloads.free", "Free")}</span>
                          : <Price size="inline" eurCents={Math.round(Number(r.price_eur) * 100)} />}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("downloads.lookInside", "Look inside")}</DialogTitle>
            <DialogDescription>{t("downloads.previewDesc", "Sample preview shows {{n}} pages out of {{total}}.", { n: row.sample_pages ?? 0, total: row.total_pages ?? "?" })}</DialogDescription>
          </DialogHeader>
          <div className="grid h-72 place-items-center rounded border border-dashed border-border bg-muted/30 text-sm text-muted-foreground">
            {t("downloads.previewSoon", "Preview reader coming soon.")}
          </div>
        </DialogContent>
      </Dialog>
    </PublicShell>
  );
}
